import type { FeeAdjustmentLineDisplay, FeeAdjustmentMember, FeeAdjustmentRecord } from "../types";
import { feeMoneyCents, feeMoneyText, isValidFeeMarkupAmount, sumFeeAdjustmentAmounts } from "./feeAdjustmentMoney";

export type MemberFeeAdjustmentSummary = {
  baselineAmount: string;
  increaseAmount: string;
  decreaseAmount: string;
  currentAmount: string;
};

/** 整数接口接入前的展示占位：精确按元向正无穷取整，不回写分摊或总额。 */
function ceilMarkupCents(cents: bigint): bigint {
  return (cents / 100n + (cents > 0n && cents % 100n !== 0n ? 1n : 0n)) * 100n;
}

/** 已有整数结果优先，缺失时使用占位；非法结果保留未知，不能冒充未处理。 */
function lineIntegerMarkupCents(line: FeeAdjustmentLineDisplay, memberId: string): bigint | null {
  const saved = line.sales_amount_integer_by_member?.[memberId];
  if (saved != null) {
    const cents = feeMoneyCents(saved);
    return cents !== null && cents % 100n === 0n ? cents : null;
  }
  if (!line.sales_amount_by_member || typeof line.sales_amount_by_member !== "object" || Array.isArray(line.sales_amount_by_member)) return null;
  const amount = feeMoneyCents(Object.hasOwn(line.sales_amount_by_member, memberId) ? line.sales_amount_by_member[memberId] : "0.00");
  return amount === null ? null : ceilMarkupCents(amount);
}

/** 明细与成员汇总共用逐行整数口径，不出钱成员不参与加价取整。 */
export function getFeeAdjustmentLineIntegerMarkupAmount(line: FeeAdjustmentLineDisplay, member: FeeAdjustmentMember): string | null {
  const cents = member.is_payer ? lineIntegerMarkupCents(line, member.roster_member_id) : 0n;
  return cents === null ? null : feeMoneyText(cents);
}

/** 两处成员详情统一按各行整数结果计算应收；历史无行记录以个人加价合计占位。 */
export function getMemberFeeAdjustmentAmounts(record: FeeAdjustmentRecord, member: FeeAdjustmentMember): { integerMarkupAmount: string | null; receivableDeltaAmount: string | null } {
  const cost = feeMoneyCents(member.cost_allocation_amount);
  const sales = feeMoneyCents(member.sales_amount);
  let integer: bigint | null = !member.is_payer ? 0n : sales === null ? null : ceilMarkupCents(sales);
  if (member.is_payer && record.lines.length) {
    integer = 0n;
    for (const line of record.lines) {
      const amount = lineIntegerMarkupCents(line, member.roster_member_id);
      if (amount === null) { integer = null; break; }
      integer += amount;
    }
  }
  const integerAmount = integer === null ? null : feeMoneyText(integer);
  const validInteger = feeMoneyCents(integerAmount);
  const receivable = cost === null || validInteger === null ? null : feeMoneyText(cost + validInteger);
  return {
    integerMarkupAmount: validInteger === null ? null : integerAmount,
    receivableDeltaAmount: feeMoneyCents(receivable) === null ? null : receivable,
  };
}

/** 汇总当前成员已完成应收，缺失金额不当零，无关成员记录跳过。 */
export function calculateMemberFeeAdjustmentSummary(baselineAmount: string | null, memberId: string, records: readonly FeeAdjustmentRecord[]): MemberFeeAdjustmentSummary | null {
  const baseline = feeMoneyCents(baselineAmount);
  if (baseline === null) return null;
  let increase = 0n;
  let decrease = 0n;
  for (const record of records) {
    if (record.status !== "processed") continue;
    const member = record.members.find((item) => item.roster_member_id === memberId);
    if (!member) continue;
    const delta = feeMoneyCents(getMemberFeeAdjustmentAmounts(record, member).receivableDeltaAmount);
    if (delta === null) return null;
    if (delta > 0n) increase += delta;
    else decrease -= delta;
  }
  return { baselineAmount: feeMoneyText(baseline), increaseAmount: feeMoneyText(increase), decreaseAmount: feeMoneyText(decrease), currentAmount: feeMoneyText(baseline + increase - decrease) };
}

export type MemberFeeMarkupPreview = {
  amount: string;
  perMemberAmount: string;
  payerCount: number;
  oldTotalAmount: string;
  newTotalAmount: string;
  deltaAmount: string;
  members: Array<{ memberId: string; memberName: string; oldMarkupAmount: string; newMarkupAmount: string; integerMarkupAmount: string | null; oldReceivableDeltaAmount: string; newReceivableDeltaAmount: string }>;
};

/** 读取保存的销售总额；历史无总额时只汇总原映射，不重分历史成员金额。 */
export function getFeeAdjustmentSalesTotal(line: FeeAdjustmentLineDisplay): string | null {
  if (line.sales_amount_total !== undefined && line.sales_amount_total !== null) {
    const cents = feeMoneyCents(line.sales_amount_total);
    return cents === null ? null : feeMoneyText(cents);
  }
  return line.sales_amount_by_member && typeof line.sales_amount_by_member === "object" && !Array.isArray(line.sales_amount_by_member)
    ? sumFeeAdjustmentAmounts(Object.values(line.sales_amount_by_member)) : null;
}

/** 总额按出钱人数四舍五入到分；修改行使用整数占位，个人应收保留其他行的整数结果。 */
export function buildMemberFeeMarkupPreview(record: FeeAdjustmentRecord, lineId: string, memberId: string, amount: string): MemberFeeMarkupPreview | null {
  if (record.status !== "processed" || !isValidFeeMarkupAmount(amount)) return null;
  const line = record.lines.find((item) => item.id === lineId);
  const payers = record.members.filter((item) => item.is_payer);
  if (!line?.sales_amount_by_member || line.can_member_markup !== true || !payers.some((item) => item.roster_member_id === memberId)) return null;
  const nextTotal = feeMoneyCents(amount)!;
  const oldTotal = feeMoneyCents(getFeeAdjustmentSalesTotal(line));
  if (oldTotal === null) return null;
  const count = BigInt(payers.length);
  const absolute = nextTotal < 0n ? -nextTotal : nextTotal;
  const rounded = absolute / count + (absolute % count * 2n >= count ? 1n : 0n);
  const newAmount = nextTotal < 0n ? -rounded : rounded;
  const unchanged = oldTotal === nextTotal && payers.every((member) => feeMoneyCents(line.sales_amount_by_member[member.roster_member_id] ?? "0.00") === newAmount);
  const nextLine = { ...line, sales_amount_by_member: Object.fromEntries(payers.map((member) => [member.roster_member_id, feeMoneyText(newAmount)])),
    sales_amount_integer_by_member: unchanged ? line.sales_amount_integer_by_member : null };
  const nextRecord = { ...record, lines: record.lines.map((item) => item.id === lineId ? nextLine : item) };
  const members: MemberFeeMarkupPreview["members"] = [];
  for (const member of payers) {
    const oldAmount = feeMoneyCents(Object.hasOwn(line.sales_amount_by_member, member.roster_member_id)
      ? line.sales_amount_by_member[member.roster_member_id] : "0.00");
    const oldReceivable = getMemberFeeAdjustmentAmounts(record, member).receivableDeltaAmount;
    const newReceivable = getMemberFeeAdjustmentAmounts(nextRecord, member).receivableDeltaAmount;
    const integer = lineIntegerMarkupCents(nextLine, member.roster_member_id);
    if (oldAmount === null || oldReceivable === null || newReceivable === null || integer === null) return null;
    members.push({ memberId: member.roster_member_id, memberName: member.member_name_snapshot,
      oldMarkupAmount: feeMoneyText(oldAmount), newMarkupAmount: feeMoneyText(newAmount),
      integerMarkupAmount: feeMoneyText(integer),
      oldReceivableDeltaAmount: oldReceivable, newReceivableDeltaAmount: newReceivable });
  }
  return { amount: feeMoneyText(nextTotal), perMemberAmount: feeMoneyText(newAmount), payerCount: payers.length, oldTotalAmount: feeMoneyText(oldTotal), newTotalAmount: feeMoneyText(nextTotal), deltaAmount: feeMoneyText(nextTotal - oldTotal), members };
}
